// «Совет на сегодня» от ИИ на экране «Сегодня»: кнопка «!» в шапке, сам совет — в панели
// (справа на десктопе, на весь экран на телефоне); готовый совет, «обновить», ошибка провайдера.
// Точка на кнопке — совет обновился, а панель с тех пор не открывали.
// Без настроенного ИИ — подсказка «ИИ не настроен — задай LLM_* в .env», остальной экран работает.

import { useState } from 'react'

import { Button, ErrorText, Sheet, Spinner } from '@/components/ui'
import { formatTime } from '@/lib/format'
import { useAdviceToday, useRefreshAdvice } from '@/pages/ai/api'
import { Disclaimer, Markdown, NotConfigured } from '@/pages/ai/parts'

const SEEN_KEY = 'telos.advice.seen'

function readSeen(): string | null {
  try {
    return localStorage.getItem(SEEN_KEY)
  } catch {
    return null
  }
}

function writeSeen(value: string) {
  try {
    localStorage.setItem(SEEN_KEY, value)
  } catch {
    // приватный режим — точка просто останется
  }
}

export function AdviceButton() {
  const advice = useAdviceToday()
  const refresh = useRefreshAdvice()
  const [open, setOpen] = useState(false)
  const [seen, setSeen] = useState(readSeen)
  const configured = advice.data?.configured === true
  const report = advice.data?.report ?? null
  const fresh = report?.status === 'ok' && report.updated_at !== seen

  const markSeen = (value: string) => {
    writeSeen(value)
    setSeen(value)
  }

  const show = () => {
    setOpen(true)
    if (report?.updated_at) markSeen(report.updated_at)
  }

  return (
    <>
      <Button icon="exclaim" onClick={show} aria-label="Совет на сегодня" title="Совет на сегодня" className="relative">
        <span>Совет</span>
        {fresh ? (
          <span className="absolute right-1.5 top-1.5 size-2 rounded-full bg-accent" aria-hidden="true" />
        ) : null}
      </Button>
      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title="Совет на сегодня"
        fullMobile
        wide
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Закрыть</Button>
            {configured ? (
              <Button
                variant="ghost"
                icon="refresh"
                loading={refresh.isPending}
                onClick={() => refresh.mutate(undefined, { onSuccess: (r) => markSeen(r.updated_at) })}
              >
                {report?.status === 'ok' ? 'Обновить' : 'Получить совет'}
              </Button>
            ) : null}
          </>
        }
      >
        {advice.isPending ? (
          <Spinner />
        ) : advice.isError ? (
          <ErrorText error={advice.error} />
        ) : !configured ? (
          <NotConfigured />
        ) : (
          <div className="grid gap-3">
            {report?.status === 'ok' && report.content_md ? (
              <>
                <Markdown text={report.content_md} />
                <p className="text-xs text-muted">
                  Обновлён в {formatTime(report.updated_at)}
                  {report.model ? ` · ${report.model}` : ''}
                </p>
              </>
            ) : report?.status === 'error' ? (
              <ErrorText>Совет не получился: {report.error}</ErrorText>
            ) : (
              <p className="text-sm text-muted">
                Совета пока нет — он появится в настроенное время, или нажми «Получить совет».
              </p>
            )}
            {refresh.isError ? <ErrorText error={refresh.error} /> : null}
            <Disclaimer />
          </div>
        )}
      </Sheet>
    </>
  )
}
